import { Drawer, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';

import { RIGHT_SIDEBAR_WIDTH } from 'src/constants';
import {
  BANNER_HEIGHT,
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
} from 'src/constants/uiConsts';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { toggleRightSidebar } from 'src/store/ui/actions';
import { RightSidebarShadow } from 'src/theme/shadows';
import { zIndex } from 'src/theme/zindex';

interface RightSidebarStyleProps {
  showBanner: boolean;
  sticky?: boolean;
  bgColor?: string;
  width?: number | string;
  disableScrolling?: boolean;
}

interface RightSidebarProps {
  sticky?: boolean;
  bgColor?: string;
  showOnMount?: boolean;
  width?: number | string;
  disableScrolling?: boolean;
}

/**
 * This methods determines the sidebar
 * top padding. When it is sticky,
 * it shows on top of the app bar (top: 0).
 * otherwise, it should be always below
 * the appbar (top: appBarHeight).
 * Considering the case when the banner
 * is showing, right sidebar should be below
 * the banner.
 * @param props
 * @returns
 */
const getTopPadding = (props: RightSidebarStyleProps, isMobile = false) => {
  let topPadding = 0;
  const { showBanner, sticky } = props || {};
  if (showBanner) {
    topPadding += BANNER_HEIGHT;
  }

  if (sticky) {
    return topPadding;
  }

  topPadding += isMobile ? MOBILE_APP_BAR_HEIGHT : DESKTOP_APP_BAR_HEIGHT;
  return topPadding;
};

const useStyles = makeStyles<RightSidebarStyleProps>()((theme, props) => ({
  drawerPaper: {
    backgroundColor: props.bgColor,
    border: 'none',
    boxShadow: RightSidebarShadow,
    overflow: props.disableScrolling ? 'hidden' : undefined,
    [theme.breakpoints.up('sm')]: {
      top: getTopPadding(props),
      height: `calc(100% - ${getTopPadding(props)}px)`,
      width: props.width || RIGHT_SIDEBAR_WIDTH,
    },
    [theme.breakpoints.down('xs')]: {
      top: getTopPadding(props, true),
      height: `calc(100% - ${getTopPadding(props, true)}px)`,
      width: '100%',
    },
  },
  drawer: {
    width: props.width || RIGHT_SIDEBAR_WIDTH,
    flexShrink: 0,
    zIndex: zIndex.rightSidebar,
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
    },
  },
}));

export const RightSidebar: React.FC<RightSidebarProps> = ({
  children,
  sticky = true,
  bgColor = '#fff',
  showOnMount = false,
  width,
  disableScrolling = false,
}) => {
  const bannerOptions = useAppSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const { classes } = useStyles({
    showBanner: Boolean(bannerOptions),
    sticky,
    bgColor,
    width,
    disableScrolling,
  });
  const dispatch = useAppDispatch();
  const isRightSidebarOpen = useAppSelector(
    (state: RootState) => state.ui.rightSideBar.isOpen,
  );
  const theme = useTheme();
  const isDesktopScreen = useMediaQuery(theme.breakpoints.up('sm'));

  // this effect is used to open the right sidebar
  // when the showOnMount is true.
  useEffect(() => {
    // if showOnMount is true and it is desktop screen, open the right sidebar
    if (showOnMount && isDesktopScreen) {
      dispatch(
        toggleRightSidebar({
          isOpen: true,
        }),
      );
    }
  }, [showOnMount, isDesktopScreen]);

  return isRightSidebarOpen ? (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={isRightSidebarOpen}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      {children}
    </Drawer>
  ) : null;
};
